"use client";

import type { APIAchievements, APIAchievementStats } from "@rosette/ts/src/business/api/api_schema.js";
import { AchievementType } from "@rosette/ts/src/business/achievement/AchievementType.js";
import { getAchievementIcon, getLockedAchievementIcon } from "@/components/icon/getAchievementIcon.js";
import { PopupButton } from "@/components/generic/popup/PopupButton.js";
import { useFormatDate } from "@rosette/web_utils/src/time/useFormatDate.js";
import Image from "next/image";
import { cn } from "@rosette/ts/src/util/cn.js";
import type {
    AchievementProgress,
    AchievementProgressMap,
} from "@/utils/achievements/achievementProgress.js";

interface AchievementIconGridProps {
    achievements: APIAchievements | null;
    achievementStats: Record<string, APIAchievementStats> | null;
    achievementProgress: AchievementProgressMap;
}

export function AchievementIconGrid({ achievements, achievementStats, achievementProgress }: AchievementIconGridProps) {
    const allAchievements = AchievementType.values();

    return (
        <div className={cn(
            "grid grid-cols-6 lg:grid-cols-8 gap-1 p-4",
            "bg-page rounded-2xl shadow-md",
        )}>
            {allAchievements.map((achievement) => {
                const achievementID = achievement.getID();
                const earnedDateString = achievements ? achievements[achievementID] : null;
                const isEarned = !!earnedDateString;
                const earnedDate = earnedDateString ? new Date(earnedDateString) : null;
                const stats = achievementStats ? achievementStats[achievementID] : null;
                const progress = achievementProgress[achievementID] ?? null;

                return (
                    <AchievementIcon
                        key={achievementID}
                        achievement={achievement}
                        earnedDate={earnedDate}
                        isEarned={isEarned}
                        stats={stats}
                        progress={progress} />
                );
            })}
        </div>
    );
}

interface AchievementIconProps {
    achievement: AchievementType;
    earnedDate: Date | null;
    isEarned: boolean;
    stats: APIAchievementStats | null;
    progress: AchievementProgress | null;
}

function AchievementIcon({ achievement, earnedDate, isEarned, stats, progress }: AchievementIconProps) {
    const formattedDate = useFormatDate(earnedDate, { verb: "Achieved" });
    const icon = isEarned
        ? getAchievementIcon(achievement)
        : getLockedAchievementIcon(achievement);

    const tooltipContent = (
        <div className="flex flex-col gap-2">
            <h4 className="m-0 text-xl font-bold text-strong">
                {achievement.getDisplayName()}
            </h4>
            <p className="m-0 text-base text-body">
                {achievement.getDesc()}
            </p>
            {!isEarned && progress && (
                <p className="m-0 text-sm text-accent">
                    {`${progress.current} / ${progress.target} ${progress.noun}`}
                </p>
            )}
            {isEarned && earnedDate && (
                <p className="m-0 text-sm text-accent">
                    {formattedDate}
                </p>
            )}
            {stats && (
                <p className="m-0 text-sm text-muted">
                    {`${stats.percentage.toFixed(1)}% of players have earned this`}
                    {stats.count !== undefined && (
                        <>
                            {" ("}
                            {stats.count}
                            &nbsp;players)
                        </>
                    )}
                </p>
            )}
        </div>
    );

    const iconContent = (
        <div className={cn(
            "relative size-full transition-transform duration-200 hover:scale-105 active:scale-95",
            !isEarned ? "brightness-60" : null,
        )}>
            <Image
                src={icon}
                alt={achievement.getDisplayName()}
                fill={true} />
        </div>
    );

    return (
        <PopupButton
            buttonClassName={cn(
                "relative w-full aspect-square p-0 border-none bg-transparent cursor-pointer rounded-sm",
                "focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2",
            )}
            contentClassName="p-4 min-w-[360px] max-w-lg"
            buttonContent={iconContent}
            darkMode={true}
            alignment="center"
            minPopupWidthPx={360}>
            {tooltipContent}
        </PopupButton>
    );
}
